import { confirmDialog, showDialog } from '@/utils/dialogs';
import React, { useState, useEffect, useCallback } from 'react';
import {
  View,
  ScrollView,
  RefreshControl,
  ActivityIndicator,
  Pressable,
  TextInput,
} from 'react-native';
import { MaterialIcons } from '@expo/vector-icons';
import { SafeAreaView } from 'react-native-safe-area-context';

// React Native Reusables Components
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { Text } from '@/components/ui/text';

// Design System
import { colors, spacing } from '@/design-system';
import { PageHeader } from '@/design-system/components';

// Services
import { apiRequest } from '../../services/apiClient';
import { useTranslation } from '../../hooks/useTranslation';
import { useToast } from '../../context/ToastContext';
import * as Sentry from '@sentry/react-native';

interface Feedback {
  id: number;
  category: string;
  message: string;
  route: string | null;
  platform: string | null;
  app_version: string | null;
  user_id: number;
  user_email: string;
  school_id: number | null;
  created_at: string;
}

interface FeedbackListResponse {
  total: number;
  items: Feedback[];
  page: number;
  page_size: number;
  total_pages: number;
}

const CATEGORY_COLORS: Record<string, string> = {
  bug: colors.error,
  suggestion: colors.info,
  praise: colors.success,
  question: colors.secondary,
  other: colors.gray[500],
};

const CATEGORY_ICONS: Record<string, keyof typeof MaterialIcons.glyphMap> = {
  bug: 'bug-report',
  suggestion: 'lightbulb-outline',
  praise: 'sentiment-satisfied-alt',
  question: 'help-outline',
  other: 'chat-bubble-outline',
};

export default function FeedbackManagementScreen() {
  const { t } = useTranslation();
  const { showToast } = useToast();
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [feedbackList, setFeedbackList] = useState<Feedback[]>([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedPlatform, setSelectedPlatform] = useState<string | null>(null);

  const categories = ['bug', 'suggestion', 'praise', 'question', 'other'];
  const platforms = ['ios', 'android', 'web'];

  const fetchFeedback = useCallback(async (pageNum: number = 1, refresh: boolean = false) => {
    try {
      if (refresh) {
        setRefreshing(true);
      } else {
        setLoading(true);
      }

      const params: Record<string, any> = {
        page: pageNum,
        page_size: 50,
      };

      if (selectedCategory) {
        params.category = selectedCategory;
      }

      if (searchQuery.trim()) {
        params.search = searchQuery.trim();
      }

      if (selectedPlatform) {
        params.platform = selectedPlatform;
      }

      const response = await apiRequest<FeedbackListResponse>({
        endpoint: '/admin/feedback',
        method: 'GET',
        params,
      });

      setFeedbackList(response.items);
      setTotalPages(response.total_pages);
      setTotal(response.total);
      setPage(response.page);
    } catch (error: any) {
      console.error('Failed to fetch feedback:', error);
      Sentry.captureException(error);
      showDialog(
        t('common.error') || 'Error',
        error?.message || 'Failed to load feedback'
      );
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [selectedCategory, searchQuery, selectedPlatform, t]);

  useEffect(() => {
    fetchFeedback(1);
  }, [fetchFeedback]);

  const onRefresh = useCallback(() => {
    fetchFeedback(page, true);
  }, [fetchFeedback, page]);

  const handleDeleteFeedback = async (feedbackId: number) => {
    const confirmed = await confirmDialog(
      t('feedback.management.deleteTitle'),
      t('feedback.management.deleteMessage'),
      t('common.delete'),
      t('feedback.cancel'),
    );
    if (!confirmed) return;

    try {
      await apiRequest({
        endpoint: `/admin/feedback/${feedbackId}`,
        method: 'DELETE',
      });

      showToast(t('feedback.management.deleteSuccess'), 'success');
      fetchFeedback(page);
    } catch (error: any) {
      console.error('Failed to delete feedback:', error);
      Sentry.captureException(error);
      showDialog(
        t('common.error'),
        error?.message || t('feedback.management.deleteError')
      );
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const renderFeedbackCard = (feedback: Feedback) => {
    const categoryColor = CATEGORY_COLORS[feedback.category] || colors.gray[500];
    const categoryIcon = CATEGORY_ICONS[feedback.category] || 'chat-bubble-outline';

    return (
      <Card key={feedback.id} style={{ marginBottom: spacing.md }}>
        <CardHeader>
          <View style={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'flex-start' }}>
            <View style={{ flex: 1 }}>
              <View style={{ flexDirection: 'row', alignItems: 'center', marginBottom: spacing.xs }}>
                <MaterialIcons name={categoryIcon} size={20} color={categoryColor} />
                <Text
                  style={{
                    marginLeft: spacing.xs,
                    fontWeight: '600',
                    color: categoryColor,
                    textTransform: 'capitalize',
                  }}
                >
                  {feedback.category}
                </Text>
              </View>
              <Text style={{ fontSize: 12, color: colors.text.muted }}>
                {feedback.user_email}
              </Text>
              <Text style={{ fontSize: 11, color: colors.text.muted, marginTop: 2 }}>
                {formatDate(feedback.created_at)}
              </Text>
            </View>
            <Pressable
              onPress={() => handleDeleteFeedback(feedback.id)}
              style={({ pressed }) => ({
                padding: spacing.sm,
                borderRadius: 8,
                backgroundColor: colors.error + '10',
                opacity: pressed ? 0.7 : 1,
              })}
            >
              <MaterialIcons name="delete" size={20} color={colors.error} />
            </Pressable>
          </View>
        </CardHeader>
        <CardContent>
          <Text style={{ color: colors.text.secondary, lineHeight: 22 }}>
            {feedback.message}
          </Text>

          {/* Metadata */}
          <View style={{ flexDirection: 'row', flexWrap: 'wrap', marginTop: spacing.md, gap: spacing.sm }}>
            {Boolean(feedback.platform) && (
              <View
                style={{
                  flexDirection: 'row',
                  alignItems: 'center',
                  backgroundColor: colors.gray[100],
                  paddingHorizontal: spacing.sm,
                  paddingVertical: 4,
                  borderRadius: 12,
                }}
              >
                <MaterialIcons name="phone-android" size={14} color={colors.gray[600]} />
                <Text style={{ marginLeft: 4, fontSize: 12, color: colors.gray[600] }}>
                  {feedback.platform}
                </Text>
              </View>
            )}
            {Boolean(feedback.app_version) && (
              <View
                style={{
                  flexDirection: 'row',
                  alignItems: 'center',
                  backgroundColor: colors.gray[100],
                  paddingHorizontal: spacing.sm,
                  paddingVertical: 4,
                  borderRadius: 12,
                }}
              >
                <MaterialIcons name="info-outline" size={14} color={colors.gray[600]} />
                <Text style={{ marginLeft: 4, fontSize: 12, color: colors.gray[600] }}>
                  {feedback.app_version}
                </Text>
              </View>
            )}
            {Boolean(feedback.route) && (
              <View
                style={{
                  flexDirection: 'row',
                  alignItems: 'center',
                  backgroundColor: colors.gray[100],
                  paddingHorizontal: spacing.sm,
                  paddingVertical: 4,
                  borderRadius: 12,
                }}
              >
                <MaterialIcons name="place" size={14} color={colors.gray[600]} />
                <Text style={{ marginLeft: 4, fontSize: 12, color: colors.gray[600] }} numberOfLines={1}>
                  {feedback.route}
                </Text>
              </View>
            )}
          </View>
        </CardContent>
      </Card>
    );
  };

  const renderFilters = () => (
    <View style={{ marginBottom: spacing.md }}>
      {/* Search Input */}
      <View
        style={{
          flexDirection: 'row',
          alignItems: 'center',
          backgroundColor: colors.surface,
          borderRadius: 12,
          paddingHorizontal: spacing.md,
          marginBottom: spacing.md,
          borderWidth: 1,
          borderColor: colors.border,
        }}
      >
        <MaterialIcons name="search" size={20} color={colors.gray[400]} />
        <TextInput
          placeholder={t('feedback.management.searchPlaceholder')}
          placeholderTextColor={colors.gray[400]}
          value={searchQuery}
          onChangeText={setSearchQuery}
          style={{
            flex: 1,
            marginLeft: spacing.sm,
            paddingVertical: spacing.md,
            color: colors.text.primary,
            fontSize: 15,
          }}
        />
        {searchQuery.length > 0 && (
          <Pressable onPress={() => setSearchQuery('')} style={({ pressed }) => ({ opacity: pressed ? 0.7 : 1 })}>
            <MaterialIcons name="close" size={20} color={colors.gray[400]} />
          </Pressable>
        )}
      </View>

      {/* Category Filters */}
      <Text style={{ fontSize: 13, fontWeight: '600', color: colors.text.secondary, marginBottom: spacing.sm }}>
        {t('feedback.management.filterByCategory')}
      </Text>
      <ScrollView horizontal showsHorizontalScrollIndicator={false} style={{ marginBottom: spacing.md }}>
        <Pressable
          onPress={() => setSelectedCategory(null)}
          style={{
            paddingHorizontal: spacing.md,
            paddingVertical: spacing.sm,
            borderRadius: 20,
            backgroundColor: selectedCategory === null ? colors.primary : colors.gray[100],
            marginRight: spacing.sm,
          }}
        >
          <Text
            style={{
              color: selectedCategory === null ? colors.text.inverse : colors.text.secondary,
              fontWeight: '600',
              fontSize: 13,
            }}
          >
            {t('feedback.management.all')} ({total})
          </Text>
        </Pressable>
        {categories.map((category) => (
          <Pressable
            key={category}
            onPress={() => setSelectedCategory(category)}
            style={{
              flexDirection: 'row',
              alignItems: 'center',
              paddingHorizontal: spacing.md,
              paddingVertical: spacing.sm,
              borderRadius: 20,
              backgroundColor: selectedCategory === category ? CATEGORY_COLORS[category] : colors.gray[100],
              marginRight: spacing.sm,
            }}
          >
            <MaterialIcons
              name={CATEGORY_ICONS[category]}
              size={16}
              color={selectedCategory === category ? colors.text.inverse : CATEGORY_COLORS[category]}
            />
            <Text
              style={{
                marginLeft: 6,
                color: selectedCategory === category ? colors.text.inverse : colors.text.secondary,
                fontWeight: '600',
                fontSize: 13,
              }}
            >
              {t(`feedback.categories.${category}`)}
            </Text>
          </Pressable>
        ))}
      </ScrollView>

      {/* Platform Filters */}
      <Text style={{ fontSize: 13, fontWeight: '600', color: colors.text.secondary, marginBottom: spacing.sm }}>
        {t('feedback.management.filterByPlatform')}
      </Text>
      <ScrollView horizontal showsHorizontalScrollIndicator={false}>
        <Pressable
          onPress={() => setSelectedPlatform(null)}
          style={{
            paddingHorizontal: spacing.md,
            paddingVertical: spacing.sm,
            borderRadius: 20,
            backgroundColor: selectedPlatform === null ? colors.primary : colors.gray[100],
            marginRight: spacing.sm,
          }}
        >
          <Text
            style={{
              color: selectedPlatform === null ? colors.text.inverse : colors.text.secondary,
              fontWeight: '600',
              fontSize: 13,
            }}
          >
            {t('feedback.management.all')}
          </Text>
        </Pressable>
        {platforms.map((platform) => (
          <Pressable
            key={platform}
            onPress={() => setSelectedPlatform(platform)}
            style={{
              paddingHorizontal: spacing.md,
              paddingVertical: spacing.sm,
              borderRadius: 20,
              backgroundColor: selectedPlatform === platform ? colors.primary : colors.gray[100],
              marginRight: spacing.sm,
            }}
          >
            <Text
              style={{
                color: selectedPlatform === platform ? colors.text.inverse : colors.text.secondary,
                fontWeight: '600',
                fontSize: 13,
                textTransform: 'capitalize',
              }}
            >
              {platform}
            </Text>
          </Pressable>
        ))}
      </ScrollView>
    </View>
  );

  const renderPagination = () => {
    if (totalPages <= 1) return null;

    return (
      <View style={{ flexDirection: 'row', justifyContent: 'center', alignItems: 'center', marginVertical: spacing.lg }}>
        <Pressable
          onPress={() => fetchFeedback(page - 1)}
          disabled={page === 1}
          style={({ pressed }) => ({
            padding: spacing.sm,
            borderRadius: 8,
            backgroundColor: page === 1 ? colors.gray[200] : colors.primary,
            marginRight: spacing.md,
            opacity: pressed && page !== 1 ? 0.8 : 1,
          })}
        >
          <MaterialIcons name="chevron-left" size={24} color={page === 1 ? colors.gray[400] : colors.text.inverse} />
        </Pressable>

        <Text style={{ color: colors.text.secondary, fontWeight: '600' }}>
          {t('feedback.management.page')} {page} {t('feedback.management.of')} {totalPages}
        </Text>

        <Pressable
          onPress={() => fetchFeedback(page + 1)}
          disabled={page === totalPages}
          style={({ pressed }) => ({
            padding: spacing.sm,
            borderRadius: 8,
            backgroundColor: page === totalPages ? colors.gray[200] : colors.primary,
            marginLeft: spacing.md,
            opacity: pressed && page !== totalPages ? 0.8 : 1,
          })}
        >
          <MaterialIcons
            name="chevron-right"
            size={24}
            color={page === totalPages ? colors.gray[400] : colors.text.inverse}
          />
        </Pressable>
      </View>
    );
  };

  if (loading && !refreshing) {
    return (
      <SafeAreaView style={{ flex: 1, backgroundColor: colors.background }}>
        <PageHeader title={t('feedback.management.title')} subtitle={t('common.loading')} />
        <View style={{ flex: 1, justifyContent: 'center', alignItems: 'center' }}>
          <ActivityIndicator size="large" color={colors.primary} />
        </View>
      </SafeAreaView>
    );
  }

  return (
    <SafeAreaView style={{ flex: 1, backgroundColor: colors.background }}>
      <PageHeader title={t('feedback.management.title')} subtitle={`${total} ${t('feedback.management.subtitle')}`} />
      <ScrollView
        contentContainerStyle={{ padding: spacing.lg }}
        refreshControl={<RefreshControl refreshing={refreshing} onRefresh={onRefresh} />}
      >
        {renderFilters()}

        {feedbackList.length === 0 ? (
          <View style={{ padding: spacing.xl, alignItems: 'center' }}>
            <MaterialIcons name="feedback" size={64} color={colors.gray[300]} />
            <Text style={{ marginTop: spacing.md, color: colors.text.muted, textAlign: 'center' }}>
              {selectedCategory || searchQuery || selectedPlatform
                ? t('feedback.management.noFeedbackFiltered')
                : t('feedback.management.noFeedback')}
            </Text>
          </View>
        ) : (
          <>
            {feedbackList.map(renderFeedbackCard)}
            {renderPagination()}
          </>
        )}
      </ScrollView>
    </SafeAreaView>
  );
}
